import React, { useState, useRef, useEffect } from "react";
import { 
  Tabs, 
  Input, 
  Button, 
  Card, 
  Space, 
  Checkbox, 
  List, 
  Typography, 
  Divider, 
  Empty, 
  Form, 
  InputNumber,
  notification,
  Select,
  Menu
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  CloseOutlined,
  PlusOutlined,
  SearchOutlined,
  MenuOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";

import { createCustomField as serviceCreateCustomField, updateCustomField as serviceUpdateCustomField, deleteCustomField as serviceDeleteCustomField } from "../../services/settingsService";
import { getCustomFields } from "../../api/custom_fields";
import { updateChecklist, updateShowChecklistSettings, getOptionVisibility, updateOptionVisibility, deleteOptionVisibility } from "../../api/checklists";
import { useEditingChecklistState } from "../../state/editingChecklistState";
import { useSettingsState } from "../../state/settingsState";
import SelectFieldOptions from "../CustomFields/SelectFieldOptions";
import SyncPanel from "./SyncPanel";
import VisualSettings from "./VisualSettings";
import FieldVisibilitySettings from "./FieldVisibilitySettings";
import ConditionalRequiredRulesEditor from "./ConditionalRequiredRulesEditor";
import AmoPrerequisiteRulesEditor from "./AmoPrerequisiteRulesEditor";
import styles from "./ChecklistEditor.module.css";

const { Title, Text } = Typography;
const { TabPane } = Tabs;


const DraggableCustomField = ({ cf, index, moveField, handleEdit, handleUpdate, handleDelete, selectedStatus, editingChecklist }) => {
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: "CUSTOM_FIELD",
    item: () => ({ 
      id: cf.id, 
      position: cf.position,
      originalPosition: cf.position
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop() && item.position !== item.originalPosition) {
        handleUpdate({
          ...cf,
          position: item.position
        });
      }
    },
  });

  const [{ isOver }, drop] = useDrop({
    accept: "CUSTOM_FIELD",
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }
      
      if (item.id === cf.id) {
        return;
      }

      const dragIndex = item.position;
      const hoverIndex = cf.position;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      
      moveField(item.id, hoverIndex);
      item.position = hoverIndex;
    },
    drop: (item, monitor) => {
      return { dropped: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drag(drop(ref));

  const itemStyle = {
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    marginBottom: '8px',
    padding: '0 8px',
    border: isOver ? '1px dashed #1890ff' : '1px solid transparent',
    background: '#fff'
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(cf.name);
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div ref={ref} style={itemStyle}>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <MenuOutlined style={{ marginRight: 12, cursor: 'grab', color: '#999' }} />
          {!isEditing ? (
            <Space>
              <Text>{cf.name}</Text>
              <Text type="secondary">({cf.type === 'C' ? 'Чекбокс' : cf.type === 'S' ? 'Селектор' : 'Мультисписок'})</Text>
            </Space>
          ) : (
            <Space>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ width: 200 }}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => {
                  handleUpdate({ ...cf, name: editName });
                  setIsEditing(false);
                }}
                size="small"
              />
              <Button
                icon={<CloseOutlined />}
                onClick={() => setIsEditing(false)}
                size="small"
              />
            </Space>
          )}
        </div>
        <div>
          {!isEditing ? (
            <>
              {(cf.type === 'S' || cf.type === 'M') && (
                <Button 
                  type="text" 
                  icon={<SettingOutlined />} 
                  onClick={() => setShowOptions(!showOptions)}
                />
              )}
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => {
                  setIsEditing(true);
                  setEditName(cf.name);
                }}
              />
            </>
          ) : (
            <>
              <Button 
                type="text" 
                icon={<SaveOutlined />} 
                onClick={() => {
                  setIsEditing(false);
                  handleUpdate({
                    ...cf,
                    name: editName
                  });
                }}
              />
              <Button 
                type="text" 
                icon={<CloseOutlined />} 
                onClick={() => setIsEditing(false)}
              />
            </>
          )}
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                onClick={() => {
                  if (cf && (typeof cf.id === 'number' || typeof cf.id === 'string')) {
                    handleDelete(cf.id);
                  } else {
                    notification.error({
                      message: 'Ошибка',
                      description: 'Невозможно удалить поле: отсутствует идентификатор',
                      placement: 'bottomRight'
                    });
                  }
                }}
              />
        </div>
      </div>
      {(cf.type === 'S' || cf.type === 'M') && showOptions && (
        <div style={{ marginLeft: 24, marginTop: 8, marginBottom: 16 }}>
          <SelectFieldOptions
            field={cf}
            statusId={selectedStatus.amo_status_id}
            checklistId={editingChecklist.id}
            onOptionsChange={(options) => {
              const updatedField = { ...cf, options };
              handleUpdate(updatedField);
            }}
          />
        </div>
      )}
    </div>
  );
};

const CheckListEditor = ({
  cfsInStatus = [],
  onUpdateChecklistName = () => {},
  onToggleField = () => {},
  onSave = () => {},
}) => {
  const { fields, selectedStatus, checklists, setChecklists, getFilteredStatuses } = useSettingsState();
  const { editingChecklist, setEditingChecklist } = useEditingChecklistState();
  const [search, setSearch] = useState("");
  const [newCfName, setNewCfName] = useState("");
  const [newCfType, setNewCfType] = useState("C");
  const [cfToEdit, setCfToEdit] = useState({ id: null });
  const [showSettingsCFSearch, setShowSettingsCFSearch] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [displaySettingsSubMenu, setDisplaySettingsSubMenu] = useState("visual");
  const [loading, setLoading] = useState(false);
  const allStatuses = getFilteredStatuses();

  if (!editingChecklist) return null;

  const handleFieldClick = (fieldId) => {
    onToggleField(fieldId);
  };

  const hCustomFieldUpdate = async (field = null) => {
    if (!field) {
      field = cfToEdit;
    }
    if (!field || (typeof field.id !== 'number' && typeof field.id !== 'string')) {
      notification.error({
        message: 'Ошибка',
        description: 'Невозможно обновить поле: отсутствует идентификатор',
        placement: 'bottomRight'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const updated = await serviceUpdateCustomField(selectedStatus.amo_status_id, editingChecklist.id, field.id, field);
      const custom_fields = updated.custom_fields || (await getCustomFields(selectedStatus.amo_status_id, editingChecklist.id));
      setEditingChecklist({ ...editingChecklist, custom_fields });
      setCfToEdit({ id: null });
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const hCustomFieldCreate = async () => {
    if (!newCfName.trim()) return;
    
    setLoading(true);
    
    try {
      const customField = await serviceCreateCustomField(selectedStatus.amo_status_id, editingChecklist.id, { name: newCfName, type: newCfType, checklist: editingChecklist.id });
      
      if (!customField) throw new Error('Не удалось создать кастомное поле');
      
      const newEditingChecklist = { ...editingChecklist, custom_fields: [...editingChecklist.custom_fields, customField] };
      
      setEditingChecklist(newEditingChecklist);
      setNewCfName("");
      setChecklists(checklists.map(c => (c.id === newEditingChecklist.id ? newEditingChecklist : c)));
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось создать кастомное поле',
        placement: 'bottomRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const hCustomFieldDelete = async (cfId) => {
    if (cfId === undefined || cfId === null) {
      notification.error({
        message: 'Ошибка',
        description: 'Невозможно удалить поле: отсутствует идентификатор',
        placement: 'bottomRight'
      });
      return;
    }
    setLoading(true);
    
    try {
      const success = await serviceDeleteCustomField(selectedStatus.amo_status_id, editingChecklist.id, cfId);
      
      if (!success) throw new Error('Не удалось удалить кастомное поле');
      
      const newEditingChecklist = {
        ...editingChecklist,
        custom_fields: editingChecklist.custom_fields.filter(cf => cf.id !== cfId),
        conditional_required_rules: (editingChecklist.conditional_required_rules || []).map(rule => ({
          ...rule,
          excluded_custom_field_ids: (rule.excluded_custom_field_ids || []).filter(id => id !== cfId)
        })),
        amo_prerequisite_rules: (editingChecklist.amo_prerequisite_rules || []).map(rule => ({
          ...rule,
          blocked_custom_field_ids: (rule.blocked_custom_field_ids || []).filter(id => id !== cfId)
        }))
      };
      
      setEditingChecklist(newEditingChecklist);
      setChecklists(checklists.map(c => (c.id === newEditingChecklist.id ? newEditingChecklist : c)));
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось удалить кастомное поле',
        placement: 'bottomRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const moveCustomField = (id, newPosition) => {
    const lastFields = [...editingChecklist.custom_fields];
    const field = editingChecklist.custom_fields.find(cf => cf.id === id);
    
    if (!field) return;
    
    const oldPosition = field.position;
    
    const updatedFields = editingChecklist.custom_fields.map(cf => {
      if (cf.id === id) {
        return { ...cf, position: newPosition };
      }
      
      if (oldPosition < newPosition && cf.position > oldPosition && cf.position <= newPosition) {
        return { ...cf, position: cf.position - 1 };
      }
      
      if (oldPosition > newPosition && cf.position >= newPosition && cf.position < oldPosition) {
        return { ...cf, position: cf.position + 1 };
      }
      
      return cf;
    });
    
    const newEditingChecklist = {
      ...editingChecklist,
      custom_fields: updatedFields
    };
    
    setEditingChecklist(newEditingChecklist);
  };

  const filteredFields = fields.filter((field) =>
    field.NAME.toLowerCase().includes(search.toLowerCase()) &&
    (field.ENTREE_DEALS === 1 || editingChecklist.fields_ids.includes(field.ID))
  );

  const items = [
    {
      key: '1',
      label: 'Кастомные поля',
      children: (
        <div style={{ height: 'calc(60vh - 100px)', overflowY: 'auto', padding: '8px' }} className={styles.scrollableContainer}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <DndProvider backend={HTML5Backend}>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: '2px', minHeight: '100px', maxHeight: '400px', overflowY: 'auto' }} className={styles.scrollableContainer}>
                {editingChecklist.custom_fields.length === 0 ? (
                  <Empty description="Нет кастомных полей" style={{ margin: '20px 0' }} />
                ) : (
                  editingChecklist.custom_fields
                    .sort((a, b) => a.position - b.position)
                    .map((cf) => (
                      <div key={cf.id}>
                        <DraggableCustomField
                          cf={cf}
                          index={cf.position}
                          moveField={moveCustomField}
                          handleEdit={() => setCfToEdit(cf)}
                          handleUpdate={hCustomFieldUpdate}
                          handleDelete={hCustomFieldDelete}
                          selectedStatus={selectedStatus}
                          editingChecklist={editingChecklist}
                        />
                      </div>
                    ))
                )}
              </div>
            </DndProvider>
            
            <Space.Compact style={{ width: '100%', marginTop: '16px' }}>
              <Select
                value={newCfType}
                onChange={setNewCfType}
                style={{ width: 120 }}
              >
                <Select.Option value="C">Чекбокс</Select.Option>
                <Select.Option value="S">Селектор</Select.Option>
                <Select.Option value="M">Мультисписок</Select.Option>
              </Select>
              <Input
                value={newCfName}
                onChange={(e) => setNewCfName(e.target.value)}
                placeholder="Название нового кастомного поля"
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={hCustomFieldCreate}
                disabled={!newCfName.trim() || loading}
                loading={loading}
              >
                Создать
              </Button>
            </Space.Compact>
          </Space>
        </div>
      )
    },
    {
      key: '2',
      label: 'Поля AMO',
      children: (
        <div style={{ height: 'calc(60vh - 100px)', overflowY: 'auto', padding: '8px' }} className={styles.scrollableContainer}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск полей"
            />
            
            <List
              bordered
              dataSource={filteredFields}
              style={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: '1px solid #d9d9d9'
              }}
              renderItem={(field) => (
                <List.Item
                  onClick={() => handleFieldClick(field.ID)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: editingChecklist.fields_ids.includes(field.ID) ? '#e6f7ff' : 'transparent'
                  }}
                >
                  <Space>
                    <Checkbox checked={editingChecklist.fields_ids.includes(field.ID)} />
                    <Text>{field.NAME}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Space>
        </div>
      )
    },
    {
      key: '3',
      label: 'Синхронизация с AMO',
      children: (
        <div style={{ height: 'calc(60vh - 100px)', overflowY: 'auto', padding: '8px' }} className={styles.scrollableContainer}>
          <SyncPanel customFields={editingChecklist.custom_fields || []} />
        </div>
      )
    },
    {
      key: '4',
      label: 'Настройки отображения',
      children: (
        <div style={{ height: 'calc(60vh - 100px)', overflowY: 'auto', padding: '8px' }} className={styles.scrollableContainer}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ width: '280px', borderRight: '1px solid #f0f0f0', paddingRight: '16px', marginRight: '16px' }}>
              <Menu
                mode="vertical"
                selectedKeys={[displaySettingsSubMenu]}
                onSelect={({ key }) => setDisplaySettingsSubMenu(key)}
                style={{ border: 'none' }}
                items={[
                  {
                    key: 'visual',
                    label: 'Визуальные настройки',
                    icon: <SettingOutlined />
                  },
                  {
                    key: 'field-visibility',
                    label: 'При выбранном поле',
                    icon: <SearchOutlined />
                  },
                  {
                    key: 'option-visibility',
                    label: 'При значении селектора',
                    icon: <MenuOutlined />
                  },
                  {
                    key: 'conditional-required',
                    label: 'Условная обязательность',
                    icon: <SettingOutlined />
                  },
                  {
                    key: 'amo-prerequisites',
                    label: 'Блокировка по AMO',
                    icon: <SettingOutlined />
                  }
                ]}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              {displaySettingsSubMenu === 'visual' && (
                <VisualSettings 
                  editingChecklist={editingChecklist} 
                  setEditingChecklist={setEditingChecklist} 
                  checklists={checklists} 
                  setChecklists={setChecklists} 
                  selectedStatus={selectedStatus} 
                  updateChecklist={updateChecklist} 
                />
              )}
              
              {displaySettingsSubMenu === 'field-visibility' && (
                <FieldVisibilitySettings 
                  editingChecklist={editingChecklist} 
                  setEditingChecklist={setEditingChecklist} 
                  checklists={checklists} 
                  selectedStatus={selectedStatus} 
                  updateShowChecklistSettings={updateShowChecklistSettings} 
                  cfsInStatus={cfsInStatus} 
                  showSettingsCFSearch={showSettingsCFSearch} 
                  setShowSettingsCFSearch={setShowSettingsCFSearch} 
                />
              )}
              
              {displaySettingsSubMenu === 'option-visibility' && (
                <Card title="Отображать при конкретном значении селектора" size="small">
                  <OptionVisibilityEditor 
                    statusId={selectedStatus.amo_status_id} 
                    checklistId={editingChecklist.id} 
                  />
                </Card>
              )}

              {displaySettingsSubMenu === 'conditional-required' && (
                <Card title="Делать поля необязательными по значению AMO-поля" size="small">
                  <ConditionalRequiredRulesEditor
                    statusId={selectedStatus.amo_status_id}
                    editingChecklist={editingChecklist}
                    setEditingChecklist={setEditingChecklist}
                    checklists={checklists}
                    setChecklists={setChecklists}
                  />
                </Card>
              )}

              {displaySettingsSubMenu === 'amo-prerequisites' && (
                <Card title="Запрещать заполнение полей чеклиста, пока AMO-поля пустые" size="small">
                  <AmoPrerequisiteRulesEditor
                    statusId={selectedStatus.amo_status_id}
                    editingChecklist={editingChecklist}
                    setEditingChecklist={setEditingChecklist}
                    checklists={checklists}
                    setChecklists={setChecklists}
                  />
                </Card>
              )}
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <Card style={{ marginBottom: 20 }}>
      <Form layout="vertical">
        <Form.Item label="Название чеклиста">
          <Input
            value={editingChecklist.name}
            onChange={(e) => onUpdateChecklistName(e.target.value)}
            placeholder="Введите название чеклиста"
          />
        </Form.Item>
        
        <Form.Item>
          <Checkbox 
            checked={editingChecklist.custom_fields_required} 
            onChange={(e) => {
              const updatedChecklist = {
                ...editingChecklist,
                custom_fields_required: e.target.checked
              };
              setEditingChecklist(updatedChecklist);
              setChecklists(checklists.map(c => c.id === updatedChecklist.id ? updatedChecklist : c));
              updateChecklist(selectedStatus.amo_status_id, editingChecklist.id, updatedChecklist);
            }}
          >
            Обязательные кастомные поля для перехода на следующий этап
          </Checkbox>
        </Form.Item>

        <Form.Item label="Статусы">
          <Select 
            mode="multiple"
            value={editingChecklist.statuses || []}
            onChange={(values) => {
              const newValues = values.includes(selectedStatus.amo_status_id)
                ? values
                : [...values, selectedStatus.amo_status_id];
              const updated = { ...editingChecklist, statuses: newValues };
              setEditingChecklist(updated);
              setChecklists(checklists.map(c => c.id === updated.id ? updated : c));
            }}
            onDeselect={(value) => {
              if (value === selectedStatus.amo_status_id) {
                const prev = editingChecklist.statuses || [];
                const restored = prev.includes(selectedStatus.amo_status_id)
                  ? prev
                  : [...prev, selectedStatus.amo_status_id];
                const updated = { ...editingChecklist, statuses: restored };
                setEditingChecklist(updated);
                setChecklists(checklists.map(c => c.id === updated.id ? updated : c));
              }
            }}
            disabled={Boolean(editingChecklist.parent)}
            style={{ width: '100%' }}
            placeholder="Выберите статусы для чеклиста"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
            listHeight={200}
            dropdownStyle={{ maxHeight: '200px' }}
          >
            {allStatuses.map((status) => (
              <Select.Option
                key={status.amo_status_id}
                value={status.amo_status_id}
              >
                {status.pipeline_name ? `${status.pipeline_name} → ${status.name}` : status.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />

      <Divider />
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button 
          type="primary" 
          size="large"
          onClick={onSave}
          style={{ minWidth: 120 }}
        >
          Сохранить чеклист
        </Button>
      </div>
    </Card>
  );
};

const OptionVisibilityEditor = ({ statusId, checklistId }) => {
  const [visibilityRules, setVisibilityRules] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedValue, setSelectedValue] = useState("");
  const [availableSelectors, setAvailableSelectors] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { checklists } = useSettingsState();

  const formatSelectorNameForSettings = (selector, checklistName) => {
    if (!selector.name.includes(`[${checklistName}]`)) {
      return `[${checklistName}] ${selector.name}`;
    }
    return selector.name;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const statusChecklists = checklists;
        let allSelectors = [];
        
        const currentChecklist = checklists.find(c => c.id === checklistId);
        
        
        if (currentChecklist?.custom_fields) {
          const currentSelectors = currentChecklist.custom_fields.filter(field => field.type === 'S' || field.type === 'M');
          if (currentSelectors.length > 0) {
            allSelectors = [...allSelectors, ...currentSelectors];
          }
        }
        
        
        if (currentChecklist?.parent) {
          const parentChecklist = checklists.find(c => c.id === currentChecklist.parent);
          if (parentChecklist?.custom_fields) {
            const parentSelectors = parentChecklist.custom_fields.filter(field => field.type === 'S' || field.type === 'M');
            if (parentSelectors.length > 0) {
              const formattedParentSelectors = parentSelectors.map(selector => ({
                ...selector,
                displayName: formatSelectorNameForSettings(selector, parentChecklist.name || 'Родительский чеклист'),
                originalName: selector.name,
                isFromParent: true
              }));
              allSelectors = [...allSelectors, ...formattedParentSelectors];
            }
          }
        } else {
          
          for (const checklist of statusChecklists) {
            if (checklist.id === checklistId) continue;
            
            const checklistSelectors = checklist.custom_fields?.filter(field => field.type === 'S' || field.type === 'M') || [];
            if (checklistSelectors.length > 0) {
              const formattedSelectors = checklistSelectors.map(selector => ({
                ...selector,
                displayName: formatSelectorNameForSettings(selector, checklist.name || 'Другой чеклист'),
                originalName: selector.name
              }));
              allSelectors = [...allSelectors, ...formattedSelectors];
            }
          }
        }
        
        setAvailableSelectors(allSelectors);
        
        try {
          const rules = await getOptionVisibility(statusId, checklistId);
          setVisibilityRules(rules);
        } catch (error) {
          console.error('Ошибка при загрузке правил видимости:', error);
        }
      } catch (error) {
        notification.error({
          message: 'Ошибка',
          description: 'Не удалось загрузить данные для настройки видимости',
          placement: 'bottomRight'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [statusId, checklistId, checklists]);

  useEffect(() => {
    if (!selectedField) {
      setAvailableOptions([]);
      return;
    }
    
    const selectedSelector = availableSelectors.find(selector => selector.id === selectedField);
    if (!selectedSelector) {
      setAvailableOptions([]);
      return;
    }
    
    if (selectedSelector.options && selectedSelector.options.length > 0) {
      setAvailableOptions(selectedSelector.options);
    } else {
      setAvailableOptions([]);
    }
  }, [selectedField, availableSelectors]);

  const addVisibilityRule = async () => {
    if (!selectedField || !selectedValue) return;
    
    setLoading(true);
    try {
      const selectedSelector = availableSelectors.find(s => s.id === selectedField);
      
      
      if (selectedSelector.isFromParent) {
        notification.warning({
          message: 'Внимание',
          description: 'Настройка видимости по полю из родительского чеклиста может влиять на отображение данного подчеклиста',
          placement: 'bottomRight'
        });
      }
      
      await updateOptionVisibility(statusId, checklistId, selectedField, selectedValue);
      
      const newRule = {
        id: Date.now(), 
        custom_field_id: selectedField,
        custom_field_name: selectedSelector.displayName || selectedSelector.name,
        option_value: selectedValue
      };
      
      setVisibilityRules([...visibilityRules, newRule]);
      setSelectedValue("");
    } catch (error) {
      
      if (error.message && error.message.includes('not found for the given checklist')) {
        notification.error({
          message: 'Ошибка',
          description: 'Невозможно настроить видимость по полю из другого чеклиста. Используйте только поля текущего чеклиста.',
          placement: 'bottomRight'
        });
      } else {
        notification.error({
          message: 'Ошибка',
          description: 'Не удалось добавить правило видимости',
          placement: 'bottomRight'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const removeVisibilityRule = async (ruleId) => {
    setLoading(true);
    try {
      await deleteOptionVisibility(statusId, checklistId, ruleId);
      setVisibilityRules(visibilityRules.filter(rule => rule.id !== ruleId));
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось удалить правило видимости',
        placement: 'bottomRight'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <List
        bordered
        loading={loading}
        dataSource={visibilityRules}
        style={{ 
          maxHeight: '200px', 
          overflowY: 'auto'
        }}
        renderItem={(rule) => (
          <List.Item
            actions={[
              <Button 
                icon={<DeleteOutlined />} 
                danger 
                onClick={() => removeVisibilityRule(rule.id)}
                size="small"
              />
            ]}
          >
            <Space>
              <span>Показывать когда "{rule.custom_field_name}" имеет значение "{rule.option_value}"</span>
            </Space>
          </List.Item>
        )}
        locale={{ emptyText: <Empty description="Нет правил видимости" /> }}
      />
      
      <Divider style={{ margin: '12px 0' }} />
      
      <Space direction="vertical" style={{ width: '100%' }}>
        <Form layout="vertical">
          <Form.Item label="Селектор или мультисписок">
            <Select
              placeholder="Выберите поле"
              value={selectedField}
              onChange={(value) => {
                setSelectedField(value);
                setSelectedValue("");
              }}
              style={{ width: '100%' }}
              disabled={loading}
              listHeight={150}
              dropdownStyle={{ maxHeight: '150px' }}
            >
              {availableSelectors.map(selector => (
                <Select.Option key={selector.id} value={selector.id}>
                  {selector.displayName || selector.name} ({selector.type === 'S' ? 'Селектор' : 'Мультисписок'})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item label="Значение">
            <Select
              placeholder="Выберите значение"
              value={selectedValue}
              onChange={setSelectedValue}
              style={{ width: '100%' }}
              disabled={loading || !selectedField || availableOptions.length === 0}
              listHeight={120}
              dropdownStyle={{ maxHeight: '120px' }}
            >
              {availableOptions.map(option => (
                <Select.Option key={option.id} value={option.value}>
                  {option.value}
                </Select.Option>
              ))}
            </Select>
            {availableOptions.length === 0 && selectedField && (
              <div style={{ color: 'red', marginTop: 8 }}>
                У выбранного поля нет опций. Добавьте их в настройках поля.
              </div>
            )}
          </Form.Item>
          
          <Button
            type="primary"
            onClick={addVisibilityRule}
            disabled={loading || !selectedField || !selectedValue}
            block
          >
            Добавить правило
          </Button>
        </Form>
      </Space>
    </Space>
  );
};

export default CheckListEditor;
